import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { CalendarEvent, DbCalendarEvent } from "./types";
import { mapDbEventToCalendarEvent } from "./event-mappers";
import { useGlobalCache, createSupabaseSubscription } from "@/hooks/use-global-cache";

export const useSessionEvents = (
  currentDate: Date, 
  isClientView: boolean, 
  clientId: string | null,
  setError: (error: Error | null) => void,
  toast: any
) => {
  // Chiave cache indipendente dal mese
  const cacheKey = isClientView 
    ? `session_events_client_${clientId}`
    : 'session_events_admin';

  // Inizializziamo un valore iniziale vuoto per evitare referenze circolari
  const initialEvents: CalendarEvent[] = [];
  
  // Utilizziamo useGlobalCache con tempi di cache più lunghi
  const { 
    data: sessionEvents = initialEvents, 
    isLoading 
  } = useGlobalCache<CalendarEvent[]>(
    cacheKey,
    async () => {
      try {
        // Prima recupera i colori dei clienti
        const { data: clientsData, error: clientsError } = await supabase
          .from("clients")
          .select("id, first_name, last_name, color_code, avatar_url");
          
        if (clientsError) {
          console.error("Errore nel caricamento dei clienti:", clientsError);
          setError(clientsError);
        }
        
        // Crea una mappa dei colori dei clienti
        const clientColorMap = new Map<string, string>();
        const clientNameMap = new Map<string, string>();
        const clientAvatarMap = new Map<string, string>();
        
        if (clientsData) {
          clientsData.forEach(client => {
            if (client && client.id && client.color_code) {
              clientColorMap.set(client.id, client.color_code);
            }
            if (client && client.id) {
              const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim();
              clientNameMap.set(client.id, fullName);
            }
            if (client && client.id && client.avatar_url) {
              clientAvatarMap.set(client.id, client.avatar_url);
            }
          });
        }
      
        // Recupera le sessioni
        let sessionsQuery;
        
        if (isClientView && clientId) {
          sessionsQuery = supabase
            .from("calendar_events")
            .select("*, clients:client_id(first_name, last_name, avatar_url)")
            .eq("client_id", clientId);
        } else {
          sessionsQuery = supabase
            .from("calendar_events")
            .select("*, clients:client_id(first_name, last_name, avatar_url)");
        }
        
        const { data: sessionsData, error: sessionsError } = await sessionsQuery;

        if (sessionsError) {
          console.error("Errore nel caricamento delle sessioni:", sessionsError);
          setError(sessionsError);
          toast({
            title: "Errore",
            description: "Impossibile caricare le sessioni per il calendario",
            variant: "destructive"
          });
          return [];
        }

        // Mappa i dati dal DB in eventi del calendario
        const events = (sessionsData || []).map(dbEvent => {
          // Estrai il nome completo del cliente
          const clientName = dbEvent.clients 
            ? `${dbEvent.clients.first_name || ''} ${dbEvent.clients.last_name || ''}`.trim() 
            : clientNameMap.get(dbEvent.client_id) || '';
            
          // Estrai l'URL dell'avatar
          const clientImage = dbEvent.clients?.avatar_url || clientAvatarMap.get(dbEvent.client_id) || '';
            
          const calendarEvent = mapDbEventToCalendarEvent(dbEvent, clientColorMap);
          return {
            ...calendarEvent,
            clientName,
            clientImage
          };
        });
        
        return events;
      } catch (error) {
        console.error("Errore nel caricamento delle sessioni:", error);
        setError(error instanceof Error ? error : new Error('Errore sconosciuto'));
        return [];
      }
    },
    {
      validity: 24 * 60 * 60 * 1000,     // 24 ore 
      staleTime: 24 * 60 * 60 * 1000,    // 24 ore
      setupRealtimeSubscription: (notifyUpdate) => {
        return createSupabaseSubscription(
          supabase,
          'calendar_events',
          {},
          notifyUpdate
        );
      }
    }
  );

  return {
    sessionEvents: sessionEvents || [], 
    isLoadingSessions: isLoading
  };
};
